
package org.example.zone360.pages.widgets.matchAnalysisWidget

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaX
import com.varabyte.kobweb.silk.components.layout.VerticalDivider
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.example.zone360.components.CustomSpacer
import org.example.zone360.componentss.FieldTitleTextNormalSimple
import org.example.zone360.models.MatchAnalysis
import org.example.zone360.models.SessionMatchAnalysis
import org.example.zone360.models.SessionTrainingPriority
import org.example.zone360.models.TrainingPriority
import org.example.zone360.theme.Palette
import org.example.zone360.utils.Res
import org.example.zone360.utils.getCurrentMilliseconds
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.HTMLCanvasElement

@Composable
fun MatchAnalysisWidget(
    data: SessionMatchAnalysis,
    widgetNo: Int,
    widgetName: String,
    widgetColor: Color,
    useNumberInsteadOfImage: Boolean = false,
    modifier: Modifier = Modifier,
    onWidgetClick: (() -> Unit)? = null
) {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint < Breakpoint.MD

    val screenWidth = window.innerWidth
    val isWideScreen = screenWidth >= 1600

    val hasData = data.score > 0 ||
            data.subScores.inPossession > 0 ||
            data.subScores.outOfPossession > 0 ||
            data.subScores.transitions > 0 ||
            data.strengths.isNotEmpty() ||
            data.weaknesses.isNotEmpty() ||
            data.rootCauses.isNotEmpty()

    Box(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .borderRadius(16.px)
            .padding(20.px)
            .border(
                1.px,
                LineStyle.Solid,
                Color.rgb(229, 231, 235)
            )
            .styleModifier {
                property("position", "relative")
                if (onWidgetClick != null) {
                    property("cursor", "pointer")
                }
            }
            .onClick {
                onWidgetClick?.invoke()
            }
    ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                HeaderSection(
                    widgetName = widgetName,
                    useNumberInsteadOfImage = useNumberInsteadOfImage,
                    widgetColor = widgetColor,
                    widgetNo = widgetNo
                )
                if (!hasData) {
                    // Show "No Data Available" message
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(200.px)
                            .margin(top = 18.px)
                            .borderRadius(14.px)
                            .border(
                                1.px,
                                LineStyle.Solid,
                                Color.rgb(229, 231, 235)
                            ),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            FieldTitleTextNormalSimple(
                                text = "No Data Available",
                                fontSize = 14.px,
                                fontWeight = 600,
                                color = Color.rgb(107, 114, 128)
                            )
                            FieldTitleTextNormalSimple(
                                text = "This session has no match analysis data yet",
                                fontSize = 12.px,
                                color = Color.rgb(156, 163, 175)
                            )
                        }
                    }
                } else {

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 18.px)
                        .borderRadius(14.px)
                        .gap(if (window.screen.width < 768) 0.px else 16.px)
                        .border(
                            1.px,
                            LineStyle.Solid,
                            Color.rgb(229, 231, 235)
                        )
                        .then(
                            if (isWideScreen) {
                                Modifier.styleModifier {
                                    property("flex-wrap", "nowrap")
                                }
                            } else {
                                Modifier.flexWrap(FlexWrap.Wrap)
                            }
                        ),
                    verticalAlignment = Alignment.Top
                ) {
                    // Overall Score Section - styled like match detail
                    Box(
                        Modifier.then(
                            if (isWideScreen) Modifier.width(22.percent)
                            else Modifier.width(100.percent)
                        )
                    ) {
                        OverallScoreSection(data, isWideScreen)
                    }
                    if (isWideScreen) {
                        Box(
                            Modifier
                                .width(1.px)
                                .height(145.px)
                                .backgroundColor(Color.rgb(229, 231, 235))
                                .align(Alignment.CenterVertically)
                        )
                    }

                    // Analysis Columns with consistent styling
                    AnalysisColumn(
                        title = "STRENGTHS",
                        titleColor = Color.rgb(34, 197, 94),
                        bulletColor = Color.rgb(34, 197, 94),
                        items = data.strengths,
                        background = Palette.ViewDetailBackground.rgbLight,
                        isMobile = isMobile,
                        isWideScreen = isWideScreen
                    )
                    if (isWideScreen) {
                        Box(
                            Modifier
                                .width(1.px)
                                .height(145.px)
                                .backgroundColor(Color.rgb(229, 231, 235))
                                .align(Alignment.CenterVertically)
                        )
                    }

                    AnalysisColumn(
                        title = "WEAKNESSES",
                        titleColor = Color.rgb(239, 68, 68),
                        bulletColor = Color.rgb(239, 68, 68),
                        items = data.weaknesses,
                        background = Palette.ViewDetailBackground.rgbLight,
                        isMobile = isMobile,
                        isWideScreen = isWideScreen
                    )
                    if (isWideScreen) {
                        Box(
                            Modifier
                                .width(1.px)
                                .height(145.px)
                                .backgroundColor(Color.rgb(229, 231, 235))
                                .align(Alignment.CenterVertically)
                        )
                    }
                    AnalysisColumn(
                        title = "ROOT CAUSES",
                        titleColor = Color.rgb(245, 158, 11),
                        bulletColor = Color.rgb(245, 158, 11),
                        items = data.rootCauses,
                        background = Palette.ViewDetailBackground.rgbLight,
                        isMobile = isMobile,
                        isWideScreen = isWideScreen
                    )
                }
            }
        }
    }
}

@Composable
private fun OverallScoreSection(
    data: SessionMatchAnalysis,
    isWideScreen: Boolean
) {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint < Breakpoint.MD
    val chartId = remember { "circularScore_${getCurrentMilliseconds()}" }

    // Cleanup on unmount
    DisposableEffect(chartId) {
        onDispose {
            val s = document.createElement("script")
            s.textContent = """
            (function(){
                var c = Chart.getChart('$chartId');
                if(c){ c.destroy(); }
            })();
        """.trimIndent()
            document.body?.appendChild(s)
        }
    }

    // Draw chart
    // Use the same color as the score value text (Green for overall score)
    val scoreColor = when {
        data.score.toInt() >= 80 -> "#22C55E"  // Green
        data.score.toInt() >= 60 -> "#22C55E"  // Green
        data.score.toInt() >= 40 -> "#2563EB"  // Blue
        else -> "#EF4444"                      // Red
    }

    LaunchedEffect(data.score) {
        delay(100)
        val canvas = document.getElementById(chartId) as? HTMLCanvasElement
            ?: return@LaunchedEffect

        val remaining = 100 - data.score.toInt()
        val size = if (isWideScreen) 70 else if (isMobile) 80 else 92

        val script = """
(function(){
    var existingChart = Chart.getChart('$chartId');
    if(existingChart){ existingChart.destroy(); }

    var canvas = document.getElementById('$chartId');
    if(!canvas) return;
    
    canvas.width  = ${size * 2};
    canvas.height = ${size * 2};

    var centerPlugin = {
        id: 'center_$chartId',
        afterDraw: function(chart) {
            var ctx = chart.ctx;
            ctx.save();
            // Larger font for the score
            ctx.font = 'bold 32px sans-serif';
            ctx.fillStyle = '$scoreColor';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('${data.score}', chart.width/2, chart.height/2 - 10);
            // Larger font for "/100"
            ctx.font = '14px sans-serif';
            ctx.fillStyle = '#6B7280';
            ctx.fillText('/100', chart.width/2, chart.height/2 + 20);
            ctx.restore();
        }
    };

    new Chart('$chartId', {
        type: 'doughnut',
        data: {
            datasets: [{
                data: [${data.score}, $remaining],
                backgroundColor: ['$scoreColor', '#ebeced'],
                borderWidth: 0.5,
                borderColor: '#ffffff'
            }]
        },
        options: {
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            cutout: '68%',
            responsive: false,
            maintainAspectRatio: false
        },
        plugins: [centerPlugin]
    });
})();
    """.trimIndent()

        val existing = document.getElementById("matchScript_$chartId")
        existing?.remove()
        val scriptEl = document.createElement("script")
        scriptEl.id = "matchScript_$chartId"
        scriptEl.textContent = script
        document.body?.appendChild(scriptEl)
    }

    Column(
        modifier = Modifier
            .padding(top = if (isWideScreen) 20.px else 18.px, leftRight = 8.px)
            .then(
                if (isWideScreen) {
                    Modifier.styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                    }
                } else {
                    Modifier
                }
            )
    ) {
        Row(
            modifier = Modifier
                .then(
                    if (isWideScreen) {
                        Modifier.styleModifier {
                            property("flex-wrap", "nowrap")
                            property("gap", "8px")
                        }
                    } else {
                        Modifier.flexWrap(FlexWrap.Wrap).gap(14.px)
                    }
                ),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = if (isWideScreen) Arrangement.Start else Arrangement.Center
        ) {
            // Canvas-based Circular Score
            Canvas(
                Modifier
                    .size(if (isWideScreen) 70.px else if (isMobile) 80.px else 92.px)
                    .id(chartId)
                    .toAttrs {
                        attr("width", if (isWideScreen) "140" else if (isMobile) "160" else "184")
                        attr("height", if (isWideScreen) "140" else if (isMobile) "160" else "184")
                        attr("style", "width:${if (isWideScreen) 70 else if (isMobile) 80 else 92}px;height:${if (isWideScreen) 70 else if (isMobile) 80 else 92}px;")
                    }
            ) {}

            Column(
                modifier = Modifier.margin(left = if (isWideScreen) 8.px else 16.px)
            ) {
                ScoreRow(
                    "In Possession",
                    data.subScores.inPossession.toInt(),
                    Color.rgb(34,197,94),  // Green
                    isWideScreen
                )

                ScoreRow(
                    "Out of Possession",
                    data.subScores.outOfPossession.toInt(),
                    Color.rgb(239,68,68),  // Red
                    isWideScreen
                )

                ScoreRow(
                    "Transitions",
                    data.subScores.transitions.toInt(),
                    Color.rgb(37,99,235),  // Blue
                    isWideScreen
                )
            }
        }
    }
}

@Composable
private fun ScoreRow(
    label: String,
    value: Int,
    valueColor: Color,
    isWideScreen: Boolean
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = if (isWideScreen) 4.px else 8.px),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        FieldTitleTextNormalSimple(
            text = label,
            fontSize = if (isWideScreen) 11.px else 14.px
        )

        FieldTitleTextNormalSimple(
            text = value.toString(),
            fontSize = if (isWideScreen) 13.px else 16.px,
            fontWeight = 700,
            marginLeft = 5.px,
            color = valueColor
        )
    }
}

@Composable
private fun AnalysisColumn(
    title: String,
    titleColor: Color,
    bulletColor: Color,
    items: List<String>,
    background: Color,
    isMobile: Boolean,
    isWideScreen: Boolean
) {
    Column(
        modifier = Modifier
            .then(
                when {
                    isMobile -> Modifier.fillMaxWidth()
                    isWideScreen -> {
                        Modifier
                            .width(22.percent)
                            .styleModifier {
                                property("flex-shrink", "0")
                            }
                    }
                    else -> Modifier.flexGrow(1)
                }
            )
            .padding(if (isWideScreen) 12.px else 16.px)
    ) {
        FieldTitleTextNormalSimple(
            text = title,
            color = titleColor,
            fontWeight = 700,
            fontSize = if (isWideScreen) 12.px else 14.px,
            marginBottom = if (isWideScreen) 8.px else 12.px
        )

        if (items.isEmpty()) {
            FieldTitleTextNormalSimple(
                text = "No data available",
                fontSize = if (isWideScreen) 11.px else 13.px,
                color = Color.rgb(107, 114, 128)
            )
        } else {
            items.take(3).forEachIndexed { index, item ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(bottom = if (isWideScreen) 4.px else 8.px),
                    verticalAlignment = Alignment.Top
                ) {
                    Box(
                        modifier = Modifier
                            .size(if (isWideScreen) 14.px else 16.px)
                            .backgroundColor(bulletColor)
                            .borderRadius(50.px)
                            .margin(top = 2.px)
                            .flexShrink(0),
                        contentAlignment = Alignment.Center
                    ) {
                        FieldTitleTextNormalSimple(
                            text = when (title) {
                                "STRENGTHS" -> "✓"
                                "WEAKNESSES" -> "✓"
                                else -> "•"
                            },
                            color = Colors.White,
                            fontSize = if (isWideScreen) 8.px else 9.px,
                            fontWeight = 700
                        )
                    }
                    FieldTitleTextNormalSimple(
                        text = "${index + 1}. $item",
                        marginLeft = if (isWideScreen) 6.px else 10.px,
                        fontSize = if (isWideScreen) 11.px else 13.px,
                        color = Color.rgb(55, 65, 81),
                    )
                }
            }
        }
    }
}

@Composable
private fun HeaderSection(
    widgetName: String,
    useNumberInsteadOfImage: Boolean = false,
    widgetNo: Int,
    widgetColor: Color = Palette.CoachTalkPrimaryBlue.rgbLight,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row( modifier = Modifier.gap(15.px),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            if (useNumberInsteadOfImage) {
                // Show widget number instead of image
                Box(
                    modifier = Modifier
                        .size(30.px)
                        .backgroundColor(widgetColor)
                        .borderRadius(6.px),
                    contentAlignment = Alignment.Center
                ) {
                    FieldTitleTextNormalSimple(
                        text = widgetNo.toString(),
                        fontSize = 16.px,
                        fontWeight = 700,
                        color = Colors.White
                    )
                }
            } else {
                Image(src = Res.Images.rising, modifier = Modifier.size(30.px))
            }
            FieldTitleTextNormalSimple(
                text = widgetName,
                fontSize = 18.px,
                fontWeight = 700,
                marginTop = 5.px
            )
        }
    }
}

@Composable
private fun TrainingPrioritiesSection(
    priorities: List<SessionTrainingPriority>
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.px)
            .borderRadius(14.px)
            .border(
                1.px,
                LineStyle.Solid,
                Color.rgb(229, 231, 235)
            )
            .backgroundColor(Palette.ViewDetailBackground.rgbLight)
    ) {
        Row(modifier = Modifier.fillMaxWidth())
        {
            FieldTitleTextNormalSimple(
                text = "TRAINING PRIORITIES",
                fontWeight = 700,
                fontSize = 15.px,
                color = Color.rgb(31, 41, 55)
            )
        }

        priorities.sortedByDescending { it.percentage }.forEach { priority ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = 12.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FieldTitleTextNormalSimple(
                    text = priority.area,
                    fontSize = 13.px,
                    width = if (window.screen.width < 768) 120.px else 160.px,
                    color = Color.rgb(55, 65, 81),
                )

                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(6.px)
                        .backgroundColor(Color.rgb(229, 231, 235))
                        .borderRadius(100.px)
                        .margin(leftRight = 12.px)
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxHeight()
                            .width(
                                priority.percentage.toInt().coerceIn(0, 100).percent
                            )
                            .backgroundColor(
                                getPriorityColor(priority.percentage)
                            )
                            .borderRadius(100.px)
                    )
                }

                FieldTitleTextNormalSimple(
                    text = "${priority.percentage}%",
                    fontSize = 13.px,
                    fontWeight = 700,
                    color = getPriorityColor(priority.percentage),
                    width = 40.px,
                    textAlign = TextAlign.End
                )
            }
        }
    }
}

private fun getPriorityColor(percentage: Long): Color {
    return when {
        percentage >= 30 -> Color.rgb(99, 102, 241)
        percentage >= 20 -> Color.rgb(59, 130, 246)
        percentage >= 10 -> Color.rgb(139, 92, 246)
        else -> Color.rgb(167, 139, 250)
    }
}
